import { defineStore, acceptHMRUpdate } from 'pinia'
import { apiClient } from '@/helpers/api.js'
import {
  INVENTORY_CARD_COLUMN_OPTIONS,
  INVENTORY_SORT_OPTIONS,
  inventoryDefaultSort,
  AVAILABLE_FILTERS,
  BASE_PARAM_KEYS,
  INVENTORY_CSV_IMPORT,
} from '@/constants'
import { useUserStore } from '@/stores/UserStore'
import { generateCardData } from "@/helpers/card.js"
import { downloadBlob } from '@/helpers/download.js'
import {
  getInventoryStatus,
  checkDismissedDuplication,
  buildInventoryQuery,
  inventoryDefaultColumnOptions,
  normalizeColumnOptionFields,
  mergeColumnOptions,
  reorderColumnOptions,
  columnOptionsInGridOrder,
  saveColumnOrder,
} from "@/helpers/inventory.js"
import { fetchMarketPricesInto, applyMarketPricesToCardStore } from "@/helpers/marketPrices.js"
import { setToLocalStorageByUserId, getFromLocalStorageByUserId } from '@/helpers/general'
import { parseBackendFilters } from '@/helpers/filters'
import { traceColumns } from '@/helpers/columnTrace'
import { handleApiError } from '@/services/errorHandler'
import { useConfigStore } from '@/stores/ConfigStore'
import { useCardStore } from '@/stores/CardStore'
import { scopedItemFilters } from '@/helpers/batch'

export const useInventoryStore = defineStore('InventoryStore', {
  state: () => {
    return {
      columnOptions: [...INVENTORY_CARD_COLUMN_OPTIONS],
      sortOption: inventoryDefaultSort(),
      inventoryDetailsLoader: false,
      fieldSearchResult: [],
      isInventory: false,
      nextCardId: null,
      previousCardId: null,
      currentFetchController: null,
      currentMarketPriceController: null,

      views: [],
      viewsLoading: false,
      selectedView: null,
      creatingView: false,
      editingView: false,
      openView: false,
      marketPrices: null,
    }
  },

  getters: {},

  actions: {
    // Initialize sortOption and columnOptions from localStorage
    initFromLocalStorage() {
      const userStore = useUserStore()
      const userId = userStore.user?.id

      /**
       * Init sortOption — re-resolved from the saved VALUE, never used as-is.
       *
       * What is persisted is the whole option OBJECT, so a stored choice keeps
       * whatever label it had the day it was saved. The 'SKU' entry has been
       * relabelled 'Card No.' (its value, `number`, is the card number and was
       * never the SKU), and anyone who had picked it would otherwise keep
       * seeing the old label — the exact confusion the rename removes.
       *
       * Matching on value also means a saved option whose label, order or
       * wording changes later just picks up the new one. A value that no longer
       * exists falls back to the saved object rather than silently resetting
       * the dealer's sort.
       */
      const savedSort = getFromLocalStorageByUserId('inventorySort', userId)
      if (savedSort) {
        this.sortOption = INVENTORY_SORT_OPTIONS.find(option => option.value === savedSort.value) || savedSort
      }

      // Init columnOptions. Under the new design `defaults` is the unified list
      // (all visible); the merge keeps whatever the user unchecked and adds any
      // column that appeared since — then writes the grown list back.
      const defaults = inventoryDefaultColumnOptions()
      const savedColumns = getFromLocalStorageByUserId('display_columns_inventory', userId)

      traceColumns('init: isNewDesign', String(userStore.isNewDesign))
      traceColumns('init: defaults', defaults.map(col => col.field))
      traceColumns('init: saved', (savedColumns || []).map(col => col.field))

      if (savedColumns?.length) {
        // Custom fields aren't in `defaults` (they come from config, and DisplayMenu
        // reconciles them) — carry the saved ones through so their visibility isn't
        // dropped on the way.
        const savedCustomFields = savedColumns.filter(col => col.field?.startsWith('custom_fields_'))
        const merged = [...mergeColumnOptions(savedColumns, defaults), ...savedCustomFields]
        this.columnOptions = merged
        traceColumns('init: merged', merged.map(col => col.field))

        // Rewrite only when the set of columns actually changed (compared by
        // field — one added + one dropped leaves the count equal).
        const savedFields = savedColumns.map(col => col.field).join('|')
        const mergedFields = merged.map(col => col.field).join('|')

        if (savedFields !== mergedFields) {
          setToLocalStorageByUserId(merged, 'display_columns_inventory', userId)
        }
      } else {
        this.columnOptions = [...defaults]
      }
    },

    updateToDefault() {
      this.columnOptions = inventoryDefaultColumnOptions()
      this.sortOption = inventoryDefaultSort()
    },

    updateColumnOption(option) {
      const userStore = useUserStore()
      this.columnOptions = this.columnOptions.reduce((acc, item) => {
        item.field === option.field ? acc.push({...item, checked: !item.checked}) :  acc.push(item)

        return acc
      }, [])

      setToLocalStorageByUserId(this.columnOptions, "display_columns_inventory", userStore.user.id)
    },

    /**
     * Reorder the columns from the Columns dropdown (drag or Alt+arrows).
     * Takes FIELDS, not indexes — hidden rows make indexes unreliable.
     *
     * Persisted to the SAME key as visibility, so a user's column arrangement —
     * which columns, and in what order — travels as one saved list.
     */
    moveColumnOption(fromField, toField) {
      const userStore = useUserStore()

      this.columnOptions = reorderColumnOptions(this.columnOptions, fromField, toField)
      traceColumns(`move ${fromField} -> ${toField}`, this.columnOptions.map(col => col.field))

      // Order gets its OWN key — `display_columns_*` is rebuilt by too many
      // things to be trusted with it. See `saveColumnOrder`.
      saveColumnOrder(this.columnOptions.map(col => col.field), userStore.user?.id)

      setToLocalStorageByUserId(this.columnOptions, "display_columns_inventory", userStore.user.id)
    },

    /**
     * The grid is the other place order can be authored (dragging a header), so
     * mirror it back into the dropdown. No-ops when nothing actually moved —
     * `columnOptionsInGridOrder` returns the same array — which keeps this from
     * bouncing against the watcher that pushes order the other way.
     */
    syncColumnOptionsOrder(orderedFields) {
      const userStore = useUserStore()
      const next = columnOptionsInGridOrder(this.columnOptions, orderedFields)

      if (next === this.columnOptions) return

      this.columnOptions = next
      setToLocalStorageByUserId(this.columnOptions, "display_columns_inventory", userStore.user.id)
      saveColumnOrder(this.columnOptions.map(col => col.field), userStore.user?.id)
    },

    updateSortOption(option) {
      const userStore = useUserStore()
      this.sortOption = option
      setToLocalStorageByUserId(option, 'inventorySort', userStore.user.id)
    },

    // ─── CSV import (CDP-2456) ────────────────────────────────────────────────
    // Errors are intentionally re-thrown (no try/catch swallow) so the modal can
    // branch on 403 → Allstar+ upgrade messaging vs. a generic failure.

    /** Download the blank import template CSV. */
    async downloadCsvImportTemplate() {
      const response = await apiClient().get(
        INVENTORY_CSV_IMPORT.ENDPOINTS.TEMPLATE,
        { responseType: 'blob' },
      )
      downloadBlob(response.data, INVENTORY_CSV_IMPORT.TEMPLATE_FILENAME)
    },

    /** Upload a CSV file to the user's default inventory. Resolves the BE payload
     *  `{ ok, job_id, row_count, max_rows }`. */
    async importInventoryCsv(file) {
      const userStore = useUserStore()
      const formData = new FormData()
      formData.append('file', file)

      const { data } = await apiClient(false, true).post(
        INVENTORY_CSV_IMPORT.ENDPOINTS.UPLOAD(userStore.user.default_inventory_id),
        formData,
      )
      return data
    },

    /** Download the row-errors CSV for a failed/partial import job. */
    async downloadCsvImportErrors(jobId) {
      const response = await apiClient().get(
        INVENTORY_CSV_IMPORT.ENDPOINTS.ERRORS(jobId),
        { responseType: 'blob' },
      )
      downloadBlob(response.data, INVENTORY_CSV_IMPORT.ERRORS_FILENAME)
    },

    /** Read a job by id — the CSV import outcome lives in `data`
     *  (`{ status, data: { created, failed, errors[] } }`). Used to confirm the
     *  async import finished before showing results / the error report. */
    async fetchCsvImportJob(jobId) {
      const { data } = await apiClient().get(INVENTORY_CSV_IMPORT.ENDPOINTS.JOB(jobId))
      return data
    },

    /**
     * Back to a clean Inventory: no filters, no saved view.
     *
     * Extracted because the same block (and the same warning comment) was
     * copy-pasted in `Sidebar/NavigationLinks.vue` and `InventorySideBarMenu.vue`,
     * and the new sidebar would have been the third copy — which is exactly how
     * one of them ends up quietly diverging.
     *
     * NOTE: this deliberately does NOT touch the saved column layout.
     * Removing `display_columns_inventory` here deleted the user's own column
     * arrangement as a side effect of NAVIGATION — reorder columns, come back to
     * Inventory from the sidebar, and the order was gone on the next load. (A
     * header drag survived because it also writes the grid's own column-state
     * key, which nothing here removes — which is exactly why dragging in the
     * spreadsheet "worked" and dragging in the dropdown didn't.) Clearing the
     * FILTERS and the selected view is this action's job; the destination page
     * re-reads the saved columns itself via `initFromLocalStorage`.
     */
    /**
     * Point Inventory at `filters` (its URL query): in store state and in the per-user localStorage
     * the chips re-read on load, so a link lands with its chips showing. Returns the route to push.
     */
    applyFilters(filters) {
      const cardStore = useCardStore()
      const userStore = useUserStore()

      cardStore.itemFilters = filters
      this.selectedView = null

      setToLocalStorageByUserId(parseBackendFilters(filters, 'inventory', cardStore), 'activeInventoryFilters', userStore.user?.id)

      return { path: '/inventory', query: filters }
    },

    /**
     * Apply a saved view: its filters, sort and columns, in store state AND in
     * the per-user localStorage the grid re-reads on load.
     *
     * Extracted from `InventorySideBarMenu.handleSelectView` — the new sidebar
     * linked straight to `/inventory/views/:id` with no query and none of this
     * state, so switching views changed the URL and nothing else. Returns the
     * route to push (query included: the filters have to be IN the URL, that is
     * what makes a view linkable and survives a reload); navigation stays with
     * the caller so the store never touches the router.
     */
    applySavedView(view) {
      const userStore = useUserStore()
      const userId = userStore.user?.id

      this.applyFilters(view.filters)

      if (view.sort) {
        setToLocalStorageByUserId(view.sort, 'inventorySort', userId)
        this.sortOption = view.sort
      } else {
        localStorage.removeItem('inventorySort')
        this.sortOption = inventoryDefaultSort()
      }

      // A view with saved columns applies them; a view WITHOUT columns leaves
      // the user's own layout alone rather than deleting it.
      if (view.columns?.length) {
        setToLocalStorageByUserId(view.columns, 'display_columns_inventory', userId)
        this.columnOptions = view.columns
      }

      this.selectedView = view

      return { path: `/inventory/views/${view.id}`, query: view.filters }
    },

    /**
     * Leave the saved-view context, keep the grid's filters.
     *
     * The sidebar's Inventory link uses this rather than the full reset:
     * unsaved filter work is unrecoverable once dropped, while a saved view is
     * one click to re-enter. Filters end only where the user says they end —
     * "Clear Filters" or saving them into a view.
     */
    clearSelectedView() {
      this.selectedView = null
    },

    resetFiltersAndView() {
      const cardStore = useCardStore()
      const userStore = useUserStore()

      cardStore.itemFilters = {}
      this.selectedView = null

      setToLocalStorageByUserId(null, 'activeInventoryFilters', userStore.user?.id)
      localStorage.removeItem('inventorySort')
    },

    /**
     * A canceled `fetchInventory` normally leaves the spinner to the request
     * that replaced it. When nothing did — the request was aborted from outside
     * and is still the current one — nobody else will ever clear it, and the
     * grid spun until a reload.
     */
    settleCanceledFetch(controller) {
      if (!controller || this.currentFetchController !== controller) return

      this.currentFetchController = null
      useCardStore().cardsLoading = false
    },

    /**
     * @param {boolean} silent  don't raise `cardsLoading` — for refreshing rows
     *   that are already on screen, where a spinner would blank the grid.
     */
    async fetchInventory({ id, perPage = 25, isPagination = false, viewId = null, silent = false }) {
      const cardStore = useCardStore()
      const userStore = useUserStore()
      // Outside the `try` so the cancel branch can tell whether a newer
      // request replaced this one.
      let controller = null

      try {
        // NOT `&& !this.views.length`. That guard meant the selected view was
        // only ever resolved when the list happened to be empty — fine while
        // nothing else fetched views, but the sidebar's Saved Views section now
        // loads them on mount, so by the time a user opened a view the list was
        // already populated, the branch was skipped, and `selectedView` stayed
        // null: no view name in the breadcrumb and no highlight in the sidebar.
        if (viewId) {
          const dataViews = this.views.length ? this.views : await this.fetchViews()

          // String-compare: route params arrive as strings, ids come back numeric.
          this.selectedView = dataViews.find(view => String(view.id) === String(viewId)) || null
          // Through `mergeColumnOptions`, not a bare normalize: a view saved
          // under the old design carries legacy ids, and the merge resolves
          // those onto real columns without depending on `isNewDesign` having
          // resolved yet (see the helper).
          this.columnOptions = this.selectedView?.columns?.length
            ? mergeColumnOptions(this.selectedView.columns, inventoryDefaultColumnOptions())
            : inventoryDefaultColumnOptions()

          if (this.selectedView?.sort) {
            this.sortOption = this.selectedView.sort
          }
        }
        
        // Cancel previous request if it exists
        if (this.currentFetchController) {
          this.currentFetchController.abort()
        }

        // Create new AbortController for current request
        controller = new AbortController()
        this.currentFetchController = controller

        if (!silent) cardStore.cardsLoading = true
        cardStore.cardsLoadFailed = false

        // Read through `scopedItemFilters`, never `cardStore.itemFilters`
        // directly: on a batch surface it guarantees `batch_id` is on the
        // request no matter who last rewrote the store.
        const itemFilters = scopedItemFilters()

        if (itemFilters.duplicate) {
          // Clear controller before return
          this.currentFetchController = null
          cardStore.hasDuplicates = true

          await this.fetchDuplicates()
          // Cleared HERE, not inside `fetchDuplicates`. That request also runs
          // in the background after every normal list load, and it is slow: it
          // used to finish in the middle of the NEXT load (the next batch, the
          // next filter) and switch that load's spinner off while its cards
          // were still on the way.
          cardStore.cardsLoading = false
          cardStore.duplicationPopup = cardStore.hasDuplicates ? checkDismissedDuplication(userStore.user.id) : false

          cardStore.cards = cardStore.duplicates

          // Apply duplicate market prices if they resolved before the list.
          this.applyMarketPrices()

          return
        }

        const configStore = useConfigStore()  

        // let inventoryList = { data: { available_filters: AVAILABLE_FILTERS } }

        // delete cardStore.itemFilters.status

        // Shared request setup — identical for the inventory list and the
        // market-prices endpoint (see `buildInventoryQuery` / `fetchMarketPrices`).
        const { query, perPage_ } = buildInventoryQuery({
          itemFilters,
          page: cardStore.page,
          userId: userStore.user.id,
          perPage,
        })

        const url = `/inventory/${id}${query}`

        // Add signal for request cancellation capability
        const { data } = await apiClient().get(url, {
          signal: controller.signal
        })

        // Check if request was canceled
        if (controller.signal.aborted) {
          this.settleCanceledFetch(controller)
          return
        }

        if (data?.has_duplicates) {
          cardStore.hasDuplicates = data.has_duplicates
          cardStore.duplicationPopup = cardStore.hasDuplicates ? checkDismissedDuplication(userStore.user.id) : false
          cardStore.duplicatesLength = data.duplicate_count
          this.fetchDuplicates()
        }

        const updatedInventory = data?.items.reduce((acc, item) => {
          item.listing = item.listing || {}
          
          // Merge card and listing data, add status fields
          acc.push({ 
            ...item.card, 
            ...item.listing, 
            status: item.card.status, 
            inventoryStatus: getInventoryStatus(item.card),
            set: item.card.set,
            number: item.card.number,
            title: item.card.title,
            description: item.card.description,
            player: item.card.player,
            team: item.card.team,
            year: item.card.year,
            id: item.card.id,
            quantity: item.card.quantity,
            listingId: item.listing?.id || null,
            collxListedStatus: item.listing?.collx_status,
            ebayListedStatus: item.listing?.ebay_status,
            whatnotListedStatus: item.listing?.whatnot_status,
            shopifyListedStatus: item.listing?.shopify_status,
            sku: item.listing?.sku || item.card?.sku,
            quantity_available: item.listing ? item.listing.quantity_available : 0,
          })
          return acc
        }, [])

        const cards = isPagination 
          ? [...cardStore.cards, ...updatedInventory] 
          : updatedInventory

        const preparedCards = []

        cards.forEach((card, index) => {
          const cardData = generateCardData(card, cardStore.selectedRanges, configStore.config)
          preparedCards.push(cardData)
        })

        cardStore.cards = preparedCards

        // Merge in market prices if they already resolved (the two requests race).
        // Centralised so whichever finishes last applies — see `applyMarketPrices`.
        this.applyMarketPrices()

        cardStore.totalCards = data.total_items_original

        // A response without facets (`null`, or none at all) threw on
        // `Object.keys` here, AFTER the rows were written — so the load was
        // reported as failed, and a batch with no rows to show yet got
        // "couldn't load this batch" instead of its empty or processing state.
        if (data.available_filters && (data.available_filters.length || Object.keys(data.available_filters).length)) {
          cardStore.availableFilters = {
            attribute: [ ...data.available_filters?.attribute ],
            brand: [ ...data.available_filters?.brand ],
            category: [ ...data.available_filters?.category ],
            graded: [ ...data.available_filters?.graded ],
            grader: [ ...data.available_filters?.grader ],
            team: [ ...data.available_filters?.team ],
            status: { ...data.available_filters?.status },
            year: [ ...data.available_filters?.year ],
            box: [ ...data.available_filters?.box ],
            row: [ ...data.available_filters?.row ],
            shelf: [ ...data.available_filters?.shelf ],
            cabinet: [ ...data.available_filters?.cabinet ],
            set: [ ...data.available_filters?.set ],
            batch: [ ...(data.available_filters?.batch || []) ], // safe fallback: backend may not return `batch` yet
            // Product Tags: `{ value, name, color, count }` per tag. The
            // backend names the facet `tag`; it is kept as `tags` here because
            // that is what the filter's own key is called on this side, and
            // renaming it would mean touching the menu, the chips and the URL
            // restore for nothing. Both spellings are read so the app doesn't
            // hang on which one arrives. Guarded spread like `batch` — a
            // response without the facet hides the filter rather than throwing.
            tags: [ ...(data.available_filters?.tag || data.available_filters?.tags || []) ],
            // Not in the tags API contract. Left in place because the "No tags"
            // row is built from it and lights up on its own if the backend
            // starts sending it; `null` keeps that row hidden until then.
            untagged_count: data.available_filters?.untagged_count ?? null,
          }
        }

        cardStore.pagination = {
          perPage: perPage_,
          currentPage: data.current_page,
          totalItems: data.total_items,
          nextPage: data.next_page ? data.current_page + 1 : null,
          prevPage: data.current_page === 1 ? null : data.current_page - 1,
          totalPages: data.total_pages
        }

        setToLocalStorageByUserId(data.total_items, 'inventoryTotalItems', userStore.user?.id)

        cardStore.cardsLoading = false
        
        // Clear controller after successful completion
        this.currentFetchController = null
      } catch (error) {
        // Ignore error if it's a request cancellation
        if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
          this.settleCanceledFetch(controller)
          return
        }
        
        cardStore.cardsLoading = false
        cardStore.cardsLoadFailed = true
        handleApiError(error, { component: 'InventoryStore', action: 'fetchInventory' })
        
        // Clear controller on error
        this.currentFetchController = null
      }
    },

    async fetchMarketPrices({ id, perPage = 25 }) {
      const cardStore = useCardStore()
      const userStore = useUserStore()

      // Dispatch by mode, mirroring how `fetchInventory` routes to
      // `fetchDuplicates`: duplicate view → the duplicates market-prices
      // endpoint (simple perPage+page query, like `fetchDuplicates`), otherwise
      // the filtered inventory market-prices endpoint (shared query builder).
      const itemFilters = scopedItemFilters()

      let url
      if (itemFilters.duplicate) {
        const perPage_ = getFromLocalStorageByUserId('perPage_inventory', userStore.user.id) || perPage
        url = `/inventory/${id}/duplicates/market-prices?perPage=${perPage_}&page=${cardStore.page}`
      } else {
        const { query } = buildInventoryQuery({
          itemFilters,
          page: cardStore.page,
          userId: userStore.user.id,
          perPage,
        })
        url = `/inventory/${id}/market-prices${query}`
      }

      // Shared loader: abort-prior + fetch + apply to the card store.
      await fetchMarketPricesInto(this, { url, context: 'InventoryStore' })
    },

    // Merge the current `marketPrices` into whatever inventory is loaded — the
    // list (`cardStore.cards`) and the open detail card. `fetchInventory` calls
    // this when it finishes so recommendations land no matter which request wins
    // the race. Idempotent + a no-op until there are both prices and cards.
    applyMarketPrices() {
      applyMarketPricesToCardStore(this.marketPrices)
    },

    async fetchInventoryById(cardId) {
      if (!cardId) return

      const userStore = useUserStore()
      const configStore = useConfigStore()
      const cardStore = useCardStore()

      try {
        this.inventoryDetailsLoader = true
        const { data } = await apiClient().get(`/inventory/${userStore.user.default_inventory_id}/${cardId}`)
        const card = { 
          ...data.card, 
          ...data.listing, 
          status: data.card.status, 
          inventoryStatus: getInventoryStatus(data.card),
          set: data.card.set,
          number: data.card.number,
          title: data.card.title,
          description: data.card.description,
          player: data.card.player,
          team: data.card.team,
          year: data.card.year,
          id: data.card.id,
          sku: data.card.sku,
          listingId: data.listing?.id || null,
          collxListedStatus: data.listing?.collx_status,
          ebayListedStatus: data.listing?.ebay_status,
          whatnotListedStatus: data.listing?.whatnot_status,
          shopifyListedStatus: data.listing?.shopify_status,
          sku: data.listing?.sku || data.card?.sku,
          quantity_available: data.listing ? data.listing.quantity_available : 0,
          collx_type_id: data.card.collx_type_id,
          sport: data.card.sport,
        }

        cardStore.selectedCardData = generateCardData(card, cardStore.selectedRanges, configStore.config)

        // Apply the market-price recommendation if prices already resolved.
        this.applyMarketPrices()

        this.inventoryDetailsLoader = false

        this.fetchNextPrevIds()
      } catch (error) {
        this.inventoryDetailsLoader = false
        handleApiError(error, { component: 'InventoryStore', action: 'fetchInventoryById' })
      }
    },

    async updatedInventoryCard(cardId, withoutLoading = false) {
      try {
        this.fetchInventoryById(cardId, withoutLoading)
      } catch (error) {
        handleApiError(error, { component: 'InventoryStore', action: 'updatedInventoryCard' })
      }
    },

    async updatedInventory(body) {
      const configStore = useConfigStore()
      const cardStore = useCardStore()

      try {
        const { data } = await apiClient().patch(`/cards/${cardStore.selectedCardData.id}`, body) 
         
        const cardData = cardStore.selectedCardData ? { ...cardStore.selectedCardData, ...data } : { ...data}
        cardStore.selectedCardData = {
          ...generateCardData(cardData, cardStore.selectedRanges, configStore.config),
          inventoryStatus: getInventoryStatus(cardData),
          listingId: cardStore.selectedCardData.listingId,
        }
      } catch (error) {
        handleApiError(error, { component: 'InventoryStore', action: 'updatedInventory' })
      }
    },

    async updateMatchedCard(selectedMatch) {
      const configStore = useConfigStore()
      const cardStore = useCardStore()

      try {
        if (!configStore.config?.hasActiveSubscription) return

        const { data } = await apiClient().post(`/cards/${cardStore.selectedCardData.id}/update-match`, { ...selectedMatch })

        if (!data) return

        cardStore.selectedCardData = generateCardData(data, cardStore.selectedRanges, configStore.config)
      } catch (error) {
        handleApiError(error, { component: 'InventoryStore', action: 'updateMatchedCard' })
      }
    },

    async fetchNextPrevIds() {
      const userStore = useUserStore()
      const cardStore = useCardStore()

      try {
        const { data } = await apiClient().get(`/inventory/${userStore.user.default_inventory_id}/prev-next/${cardStore.selectedCardData.id}`)

        if (!data) return

        const { next_card_id, prev_card_id, total_cards, current_index } = data

        this.nextCardId = next_card_id
        this.previousCardId = prev_card_id

        cardStore.totalCards = total_cards
        cardStore.cardIndex = current_index
      } catch (error) {
        handleApiError(error, { component: 'InventoryStore', action: 'fetchNextPrevIds' })
      }
    },

    async fetchDuplicates(perPage = 25) {
      const userStore = useUserStore()
      const cardStore = useCardStore()
      const configStore = useConfigStore()

      try {
        const perPage_ = getFromLocalStorageByUserId(`perPage_inventory`, userStore.user.id) || perPage

        cardStore.cardsLoading = true

        const { data } = await apiClient().get(`/inventory/${userStore.user.default_inventory_id}/duplicates?perPage=${perPage_}&page=${cardStore.page}`)
        cardStore.duplicatesLength = data.total_items

        const updatedDuplicates = []

        /**
         * Name the group the way a person would read it — the card, then how
         * many copies. The header row used to carry nothing but its id, so the
         * title cell fell through to its "add a title" placeholder and each
         * group separator rendered as an odd empty line.
         */
        const groupLabel = (group) => {
          const first = group.cards?.[0]?.card || {}
          const name = first.title
            || [first.year, first.set, first.number && `#${first.number}`, first.player]
              .filter(Boolean).join(' ')
          const count = group.cards?.length || 0

          return [name || 'Duplicate group', count ? `(${count} copies)` : '']
            .filter(Boolean).join(' ')
        }

        data.items.forEach(item => {
          updatedDuplicates.push({
            id: item.collx_match_id,
            collx_match_id: item.collx_match_id,
            isHeader: true,
            headerLabel: groupLabel(item),
          })

          item.cards.forEach((item, index) => {
            const preparedCard = { 
              ...item.card, 
              ...item.listing, 
              status: item.card.status, 
              inventoryStatus: getInventoryStatus(item.card),
              set: item.card.set,
              number: item.card.number,
              title: item.card.title,
              description: item.card.description,
              player: item.card.player,
              team: item.card.team,
              year: item.card.year,
              id: item.card.id,
              sku: item.card.sku,
              listingId: item.listing?.id || null,
              collxListedStatus: item.listing?.collx_status,
              ebayListedStatus: item.listing?.ebay_status,
              whatnotListedStatus: item.listing?.whatnot_status,
              shopifyListedStatus: item.listing?.shopify_status,
              sku: item.listing?.sku || item.card?.sku,
              quantity_available: item.listing ? item.listing.quantity_available : 0,
              quantity: item.card.quantity,
            }
            
            const cardData = generateCardData(preparedCard, cardStore.selectedRanges, configStore.config)
            updatedDuplicates.push(cardData)
          })
        })

        // cardStore.pagination = {
        //   perPage: perPage_,
        //   currentPage: data.current_page,
        //   totalItems: data.total_items,
        //   nextPage: data.next_page ? data.current_page + 1 : null,
        //   prevPage: data.current_page === 1 ? null : data.current_page - 1,
        //   totalPages: data.total_pages
        // }

        cardStore.duplicates = updatedDuplicates
      } catch (error) {
        handleApiError(error, { component: 'InventoryStore', action: 'fetchDuplicates' })
      }
    },

    async addCardsFromBatchToInventory(batchId) {
      const cardStore = useCardStore()
      const arrayOfIds = []

      for (let i = 0; i < cardStore.selectedRanges.length; i++) {
        cardStore.cards[cardStore.selectedRanges[i].startIndex]?.id ? arrayOfIds.push(cardStore.cards[cardStore.selectedRanges[i].startIndex].id) : arrayOfIds.push(cardStore.cards[0].id)
      }

      try {
        await apiClient().post(`/inventory/add-cards`, { cards: arrayOfIds })
      } catch (error) {
        handleApiError(error, { component: 'InventoryStore', action: 'addCardsFromBatchToInventory' })
      }
    },

    async fetchViews() {
      try {
        this.viewsLoading = true
        const { data } = await apiClient().get(`/user/saved-views`)
        this.views = data?.saved_views || []
        this.viewsLoading = false

        return this.views
      } catch (error) {
        this.viewsLoading = false
        handleApiError(error, { component: 'InventoryStore', action: 'fetchViews' })
      }
    },

    async createView(body) {
      const userStore = useUserStore()
      try {
        const { data } = await apiClient().post(`/user/saved-views`, { ...body, columns: body.columns ?? [], favorite: !this.views.length ? true : false })
        this.views = data?.saved_views || this.views
        userStore.user.saved_views = data?.saved_views || userStore.user.saved_views

        return this.views[this.views.length - 1]
      } catch (error) {
        handleApiError(error, { component: 'InventoryStore', action: 'createView' })
      }
    },

    async deleteView(viewId) {
      const userStore = useUserStore()
      try {
        const { data } = await apiClient().delete(`/user/saved-views/${viewId}`)
        this.views = data?.saved_views || this.views
        userStore.user.saved_views = data?.saved_views || userStore.user.saved_views

        return this.views
      } catch (error) {
        handleApiError(error, { component: 'InventoryStore', action: 'deleteView' })
      }
    },

    async updateView(viewId, body) {
      try {
        const { data } = await apiClient().put(`/user/saved-views/${viewId}`, { ...body, columns: body.columns ?? [] })
        this.views = data?.saved_views || this.views
        // Use the updated view from the response (has id), fall back to merging id into body
        this.selectedView = data?.saved_views?.find(v => v.id === viewId) || { ...body, id: viewId }
      } catch (error) {
        handleApiError(error, { component: 'InventoryStore', action: 'updateView' })
      }
    }
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useInventoryStore, import.meta.hot))
}